import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Import Modular Components
import Layout, { Branding } from './game/Layout';
import LoginCode from './game/LoginCode';
import LoginEmail from './game/LoginEmail';
import Registration from './game/Registration';
import ActiveGame from './game/ActiveGame';
import FutureEvents from './game/FutureEvents';
import ProfileEdit from './game/ProfileEdit';
import EventRegistration from './game/EventRegistration';
import ScratchMatches from './game/ScratchMatches';
import MyMatches from './game/MyMatches';
import Chat from './game/Chat';
import PastEvents from './game/PastEvents';
import Messages from './game/Messages';

export default function GameView() {
    // --- States ---
    const [participant, setParticipant] = useState(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [activeChat, setActiveChat] = useState(null);
    const activeChatRef = React.useRef(activeChat);
    useEffect(() => { activeChatRef.current = activeChat; }, [activeChat]);

    const [unreadCount, setUnreadCount] = useState(0);
    const [unreadLatest, setUnreadLatest] = useState(null);
    const [allMatches, setAllMatches] = useState([]);
    const [shareLastName, setShareLastName] = useState(true);
    const [sharePhone, setSharePhone] = useState(true);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [preferencesSaved, setPreferencesSaved] = useState(false);
    // Sezione di provenienza della chat aperta ('matches' | 'messages') per il tasto back
    const chatReturnRef = React.useRef('matches');
    const saveTokenToIDB = (token) => {
        const request = indexedDB.open("SolterosDB", 1);
        request.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains("config")) {
                db.createObjectStore("config");
            }
        };
        request.onsuccess = (e) => {
            const db = e.target.result;
            const transaction = db.transaction("config", "readwrite");
            transaction.objectStore("config").put(token, "token");
        };
    };

    function urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    const subscribeToPush = async (activeToken) => {
        if (!('serviceWorker' in navigator)) return;

        if (Notification.permission === 'denied') {
            console.warn('Push Notifications denied by user');
            return;
        }
        
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array('BIvSOBLOCF8BjwgWTCqD5iKtXurMmDB0JcDPO0qZVoMc4YtiZ5tfvuvn1622xYhJ41_TFnX2iI5eghl21W-dLi8')
            });
            
            await axios.post('/speeddate/api/subscribe', {
                token: activeToken,
                subscription: subscription
            });
            console.log('Push Subscribed');
        } catch (err) {
            if (err.name === 'NotAllowedError') {
                console.warn('Push Notifications permission denied during subscribe');
            } else {
                console.error('Push Subscribe Error:', err);
            }
        }
    };
    const [token, setToken] = useState('');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [loginMode, setLoginMode] = useState('token'); // 'token' or 'email'
    const [participations, setParticipations] = useState([]);
    const [needsRegistration, setNeedsRegistration] = useState(false);
    const [registrationData, setRegistrationData] = useState(null);
    const [futureEvents, setFutureEvents] = useState([]);    const [others, setOthers] = useState([]);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [noteDrafts, setNoteDrafts] = useState({});
    const [savingStatus, setSavingStatus] = useState({});
    const [isCheckingToken, setIsCheckingToken] = useState(false);
    const [selectedEventForReg, setSelectedEventForReg] = useState(null);
    const [registerEventId, setRegisterEventId] = useState(null);
    const [allParticipations, setAllParticipations] = useState([]);
    const [revealingMatchesFor, setRevealingMatchesFor] = useState(null);
    const [isForcedEmail, setIsForcedEmail] = useState(false);
    // Sezione attiva (hamburger menu): 'home' | 'my-events' | 'matches' | 'profile'
    const [activeSection, setActiveSection] = useState('home');
    // True quando l'utente entra con token nella querystring → default su "I Miei Eventi"
    const enteredFromUrlToken = React.useRef(false);
    
    const timeouts = React.useRef({});
    const autoRefreshInterval = React.useRef(null);

    // --- Auto-open event registration from URL param ---
    useEffect(() => {
        if (registerEventId && futureEvents.length > 0 && !selectedEventForReg) {
            const targetEvent = futureEvents.find(e => String(e.id) === String(registerEventId));
            if (targetEvent) {
                setSelectedEventForReg(targetEvent);
            }
        }
    }, [registerEventId, futureEvents, selectedEventForReg]);

    // --- Lifecycle ---
    useEffect(() => {
        const path = window.location.pathname;
        if (path.includes('/login')) {
            setLoginMode('email');
            setIsForcedEmail(true);
        }

        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token') || params.get('TOKEN');
        const urlEventId = params.get('eventId');
        const savedToken = localStorage.getItem('participant_token');
        const savedTime = localStorage.getItem('participant_token_time');

        // Store eventId from URL for auto-registration after login
        if (urlEventId) {
            setRegisterEventId(urlEventId);
            setLoginMode('email');
            setIsForcedEmail(true);
            // Pre-fetch future events so we can find the target event after login
            fetchFutureEvents();
        }
        
        let activeToken = urlToken;
        if (!activeToken && savedToken && savedTime) {
            // Ripristiniamo a 24 ore come richiesto
            if (new Date().getTime() - parseInt(savedTime) < 24 * 60 * 60 * 1000) activeToken = savedToken;
            else { localStorage.removeItem('participant_token'); localStorage.removeItem('participant_token_time'); }
        }

        if (activeToken) {
            if (urlToken) enteredFromUrlToken.current = true;
            setIsCheckingToken(true);
            handleVerify(activeToken).finally(() => {
                setIsCheckingToken(false);
                subscribeToPush(activeToken);
            });
        }

        // Request notification permission
        if ("Notification" in window) {
            Notification.requestPermission();
        }
        // Unread messages polling
        const checkUnread = async () => {
            const currentToken = localStorage.getItem('participant_token');
            if (!currentToken) return;
            try {
                const res = await axios.get(`/speeddate/api/messages/unread?token=${currentToken.toUpperCase()}`);
                const count = res.data.count;
                setUnreadLatest(res.data.latest);
                
                setUnreadCount(prevCount => {
                    if (count > 0 && count > prevCount) {
                        // New message notification
                        const latest = res.data.latest;
                        // Use a ref for activeChat to avoid closure issues
                        if (latest && activeChatRef.current?.userId !== latest.senderId) {
                            setTimeout(() => {
                                if ("Notification" in window && Notification.permission === "granted") {
                                    new Notification("Solteros: Nuovo Messaggio!", {
                                        body: "Hai ricevuto un nuovo messaggio da un tuo match.",
                                        icon: "/speeddate/logo192.png"
                                    });
                                }
                                if (window.Swal) {
                                    window.Swal.fire({
                                        title: 'Nuovo Messaggio!',
                                        text: `Hai ricevuto un nuovo messaggio da un tuo match.`,
                                        icon: 'info',
                                        toast: true,
                                        position: 'top-end',
                                        showConfirmButton: false,
                                        timer: 3000
                                    });
                                }
                            }, 0);
                        }
                    }
                    return count;
                });
            } catch (err) {}
        };

        checkUnread(); // Esegui subito al caricamento
        const unreadInterval = setInterval(checkUnread, 10000);

        return () => {
            if (autoRefreshInterval.current) clearInterval(autoRefreshInterval.current);
            clearInterval(unreadInterval);
        };
    }, []);

    useEffect(() => {
        if (!participant || !participant.id || !participant.EventId) return;
        
        const silentRefresh = async () => {
            try {
                const [pRes, othersRes] = await Promise.all([
                    axios.get(`/speeddate/api/users/verify?token=${participant.token.toUpperCase()}`),
                    participant.Event?.status >= 3 && participant.Event?.status < 5 ? axios.get(`/speeddate/api/participants/others?token=${participant.token.toUpperCase()}`) : Promise.resolve(null)
                ]);
                const p = pRes.data.success ? pRes.data.participant : pRes.data;
                setParticipant(p);
                if (othersRes) setOthers(othersRes.data);
            } catch (err) {}
        };

        if (autoRefreshInterval.current) clearInterval(autoRefreshInterval.current);
        autoRefreshInterval.current = setInterval(silentRefresh, 20000);
        
        if (participant.MatchEmailSent && !results) fetchResults(participant.token);
        
        return () => clearInterval(autoRefreshInterval.current);
    }, [participant?.id, participant?.EventId]);

    // --- Actions ---
    const handleVerify = async (providedToken) => {
        const t = providedToken || token;
        if (!t) return;
        setLoading(true);
        try {
            const res = await axios.get(`/speeddate/api/users/verify?token=${t.toUpperCase()}`);
            if (res.data.isNewUser) setIsEditingProfile(true);

            // If profile is incomplete, show registration form
            if (res.data.needsRegistration) {
                // Skip registration for game codes of non-archived events (status != 99)
                const partEv = res.data.participant;
                const isNotArchived = partEv?.EventId && partEv?.Event?.status !== 99;
                if (!isNotArchived) {
                    setNeedsRegistration(true);
                    setRegistrationData(res.data.participant);
                    setParticipant(null);
                    setLoading(false);
                    return;
                }
            }

            const p = res.data.success ? res.data.participant : res.data;
            
            if (p && p.id) {
                // If this is a User object, it might have many participations. 
                // Let's find one that is currently active (status 3 or 4)
                let activeP = p;
                const parts = p.Participations || p.participations || [];
                const active = parts.find(pp => pp.Event && pp.Event.status >= 3 && pp.Event.status < 5);
                if (active) {
                    activeP = { ...active, token: p.token || active.token };
                }

                setParticipant(activeP);
                setToken(t.toUpperCase());
                saveTokenToIDB(t.toUpperCase());
                localStorage.setItem('participant_token', t.toUpperCase());
                localStorage.setItem('participant_token_time', new Date().getTime().toString());
                setActiveSection(enteredFromUrlToken.current ? 'my-events' : 'home');
                
                if (activeP.EventId) {
                    if (activeP.Event?.status >= 3 && activeP.Event?.status < 5) fetchOthers(activeP.token);
                    if (activeP.MatchEmailSent) fetchResults(activeP.token);
                }
                fetchFutureEvents();

                // Also fetch all past participations (works for both Participant and User tokens)
                try {
                    const pastRes = await axios.get(`/speeddate/api/users/past-participations?token=${t.toUpperCase()}`);
                    if (pastRes.data.participations?.length > 0) {
                        setAllParticipations(pastRes.data.participations);
                    }
                } catch (_) {}

                return activeP;
            } else {
                handleLogout();
            }
        } catch (err) {
            if (err.response && (err.response.status === 401 || err.response.status === 404)) {
                handleLogout();
            }
            if (!providedToken) {
                window.Swal.fire({
                    title: 'Accesso Fallito',
                    text: 'Controlla il codice inserito o la connessione.',
                    icon: 'error',
                });
            }
        } finally { setLoading(false); }
    };

    const handleLogout = () => {
        localStorage.removeItem('participant_token');
        localStorage.removeItem('participant_token_time');
        setParticipant(null);
        setToken('');
        setParticipations([]);
        setNeedsRegistration(false);
        setRegistrationData(null);
        setIsEditingProfile(false);
        setEmail('');
        setOtpSent(false);
        enteredFromUrlToken.current = false;
        setActiveSection('home');
    };

    const fetchFutureEvents = async () => {
        try {
            const currentToken = token || localStorage.getItem('participant_token');
            const res = await axios.get('/speeddate/api/events/future', {
                headers: { 'x-user-token': currentToken }
            });
            setFutureEvents(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchResults = async (t) => {
        try {
            const res = await axios.get(`/speeddate/api/participants/results?token=${t.toUpperCase()}`);
            setResults(res.data);
        } catch (err) {}
    };

    const fetchOthers = async (t) => {
        try {
            const res = await axios.get(`/speeddate/api/participants/others?token=${t.toUpperCase()}`);
            setOthers(res.data);
        } catch (err) {}
    };

    const fetchAllMatches = async () => {
        const currentToken = token || localStorage.getItem('participant_token');
        if (!currentToken) {
            setAllMatches([]);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(`/speeddate/api/users/all-matches?token=${currentToken.toUpperCase()}`);
            const data = Array.isArray(res.data) ? res.data : [];
            setAllMatches(data);
        } catch (err) {
            console.error(err);
            setAllMatches([]);
            window.Swal.fire({
                title: 'Errore',
                text: 'Impossibile recuperare i match. Riprova più tardi.',
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleVote = async (targetId, choice, isMagic) => {
        if (!participant) return;
        // Optimistic update
        setOthers(prev => prev.map(o => o.id === targetId ? { ...o, ReceivedVotes: [{ choice, isMagic }] } : o));
        
        try {
            await axios.post('/speeddate/api/participants/vote', {
                token: participant.token,
                targetId,
                choice,
                isMagic,
                note: noteDrafts[targetId] || ''
            });
        } catch (err) {
            window.Swal.fire({ title: 'Errore', text: 'Voto non salvato. Riprova.', icon: 'error' });
        }
    };

    const handleSavePreferences = async (prefs) => {
        let currentShareLastName = shareLastName;
        let currentSharePhone = sharePhone;
        let currentPrivacyAccepted = privacyAccepted;

        if (prefs.shareLastName !== undefined) {
            currentShareLastName = prefs.shareLastName;
            setShareLastName(currentShareLastName);
        }
        if (prefs.sharePhone !== undefined) {
            currentSharePhone = prefs.sharePhone;
            setSharePhone(currentSharePhone);
        }
        if (prefs.privacyAccepted !== undefined) {
            currentPrivacyAccepted = prefs.privacyAccepted;
            setPrivacyAccepted(currentPrivacyAccepted);
        }
        if (prefs.save) {
            if (!participant || !participant.token) return;
            try {
                await axios.post('/speeddate/api/participants/save-preferences', {
                    token: participant.token,
                    shareLastName: currentShareLastName,
                    sharePhone: currentSharePhone,
                    privacyAccepted: currentPrivacyAccepted
                });
                setPreferencesSaved(true);
                window.Swal.fire({
                    title: 'Preferenze salvate!',
                    text: 'Le tue preferenze di condivisione sono state salvate per questo evento.',
                    icon: 'success',
                    timer: 3000,
                    showConfirmButton: false
                });
            } catch (err) {
                window.Swal.fire({ title: 'Errore', text: 'Impossibile salvare le preferenze. Riprova.', icon: 'error' });
            }
        }
    };

    const handleNoteChange = (otherId, note) => {
        setNoteDrafts(prev => ({ ...prev, [otherId]: note }));
        if (timeouts.current[otherId]) clearTimeout(timeouts.current[otherId]);
        
        timeouts.current[otherId] = setTimeout(async () => {
            const currentOther = others.find(o => o.id === otherId);
            const vote = currentOther?.ReceivedVotes?.[0];
            if (vote) {
                try {
                    await axios.post('/speeddate/api/participants/vote', {
                        token: participant.token,
                        targetId: otherId,
                        choice: vote.choice,
                        isMagic: vote.isMagic,
                        note
                    });
                } catch (err) {}
            }
        }, 1000);
    };

    // --- Login Handlers ---
    const handleLoginEmail = async (emailAddr) => {
        setLoading(true);
        try {
            const res = await axios.post('/speeddate/api/users/request-otp', { email: emailAddr });
            if (res.data.success) {
                setEmail(emailAddr);
                setOtpSent(true);
                setActiveSection('home');
                console.log("SW version:", navigator.serviceWorker.controller ? "attivo" : "mancante");
                window.Swal.fire({ title: 'Codice inviato, controlla la tua email!', icon: 'success', timer: 2000, showConfirmButton: false, toast: true, position: 'top-end' });
            }
        } catch (err) {
            window.Swal.fire({ title: 'Errore', text: 'Email non trovata o errore server.', icon: 'error' });
        } finally { setLoading(false); }
    };

    const handleVerifyOTP = async (code) => {
        setLoading(true);
        try {
            const res = await axios.post('/speeddate/api/users/verify-otp', { email, otp: code });
            if (res.data.success) {
                const parts = res.data.participations || [];
                const user = res.data.user;

                // Store all participations for the PastEvents component
                setAllParticipations(parts);

                // Filter out participations with archived events (status 99)
                const validParts = parts.filter(p => !p.Event || p.Event.status !== 99);

                // Find an active/ongoing game participation (status 3 or 4)
                const activeP = validParts.find(p => p.Event && p.Event.status >= 3 && p.Event.status < 5);

                if (res.data.needsRegistration) {
                    setNeedsRegistration(true);
                    setRegistrationData(res.data.user);
                } else if (activeP) {
                    // There is an active game - go straight to it using participant token
                    handleVerify(activeP.token);
                } else if (validParts.length > 0) {
                    handleVerify(validParts[0].token);
                } else {
                    // No participations and no token - show events directly
                    fetchFutureEvents();
                    setParticipant(user);
                    setAllParticipations([]);
                    setActiveSection('home');
                }
            }
        } catch (err) {
            window.Swal.fire({ title: 'Errore', text: 'Codice non valido o scaduto.', icon: 'error' });
        } finally { setLoading(false); }
    };

    const handleCompleteRegistration = async (formData) => {
        setLoading(true);
        try {
            const finalEmail = email || participant?.email || formData.email;
            const res = await axios.post('/speeddate/api/users/complete-registration', { ...formData, email: finalEmail });
            if (res.data.success) {
                const p = res.data.participant;
                setParticipant(p);
                setNeedsRegistration(false);
                setRegistrationData(null);
                setActiveSection('home');
                if (p.token) {
                    setToken(p.token);
                    localStorage.setItem('participant_token', p.token);
                    localStorage.setItem('participant_token_time', new Date().getTime().toString());
                }
                fetchFutureEvents();
            }
        } catch (err) {
            const msg = err.response?.data?.error || 'Impossibile completare la registrazione.';
            window.Swal.fire({ title: 'Errore', text: msg, icon: 'error' });
        } finally { setLoading(false); }
    };
    const handleBellClick = async () => {
        // Campanella (non letti) → Messaggi Privati; cuore → I Miei Match
        if (activeChat) setActiveChat(null);
        setActiveSection(unreadCount > 0 ? 'messages' : 'matches');
        fetchAllMatches();
    };

    // --- Hamburger menu ---
    const buildMenuItems = () => [
        { key: 'home', label: 'Eventi Disponibili', icon: 'fas fa-calendar-alt' },
        { key: 'my-events', label: 'I Miei Eventi', icon: 'fas fa-history' },
        { key: 'matches', label: 'I Miei Match', icon: 'fas fa-heart' },
        { key: 'messages', label: 'Messaggi Privati', icon: 'fas fa-comment-dots', badge: unreadCount > 0 ? unreadCount : null },
        { key: 'profile', label: 'Modifica Profilo', icon: 'fas fa-user-cog' },
        { key: 'logout', label: 'Esci', icon: 'fas fa-sign-out-alt', danger: true },
    ];

    const buildGameMenuItems = () => [
        { key: 'matches', label: 'I Miei Match', icon: 'fas fa-heart' },
        { key: 'profile', label: 'Modifica Profilo', icon: 'fas fa-user-cog' },
        { key: 'logout', label: 'Esci', icon: 'fas fa-sign-out-alt', danger: true },
    ];

    const handleNavigate = (key) => {
        if (key === 'logout') { handleLogout(); return; }
        // Clear chat when navigating from chat view
        if (activeChat) setActiveChat(null);
        if (key === 'matches') {
            setActiveSection('matches');
            fetchAllMatches();
            return;
        }
        if (key === 'messages') {
            setActiveSection('messages');
            fetchAllMatches();
            return;
        }
        setActiveSection(key);
    };

    // --- View Routing ---
    // 1. If we have a participant, check if profile is complete
    if (participant) {
        // Shared shell props for logged-in screens (hamburger menu sempre disponibile)
        const shellUser = {
            photo: participant.photo,
            firstName: participant.firstName,
            lastName: participant.lastName,
            email: participant.email
        };
        const shellProps = {
            unreadCount,
            onBellClick: handleBellClick,
            user: shellUser,
            menuItems: buildMenuItems(),
            activeKey: activeSection,
            onNavigate: handleNavigate
        };

        if (!participant.firstName) {
            return (
                <Layout {...shellProps} menuItems={[]}>
                    <Registration 
                        onComplete={handleCompleteRegistration} 
                        initialEmail={email || participant.email} 
                        initialData={participant}
                        loading={loading}
                    />
                </Layout>
            );
        }

        if (isEditingProfile) return (
            <Layout {...shellProps} activeKey="profile">
                <ProfileEdit 
                    participant={participant} 
                    token={token}
                    onUpdate={(updated) => { setParticipant(updated); setIsEditingProfile(false); setActiveSection('home'); }} 
                    onCancel={() => { setIsEditingProfile(false); setActiveSection('home'); }} 
                />
            </Layout>
        );

        if (activeSection === 'profile') return (
            <Layout {...shellProps} activeKey="profile">
                <ProfileEdit 
                    participant={participant} 
                    token={token}
                    onUpdate={(updated) => { setParticipant(updated); setActiveSection('home'); }} 
                    onCancel={() => setActiveSection('home')} 
                />
            </Layout>
        );

        if (revealingMatchesFor) return (
            <Layout {...shellProps} activeKey="my-events">
                <ScratchMatches 
                    participation={revealingMatchesFor}
                    onBack={() => { setRevealingMatchesFor(null); setActiveSection('my-events'); }}
                    onChat={(match) => { chatReturnRef.current = 'my-events'; setActiveChat(match); setRevealingMatchesFor(null); }}
                />
            </Layout>
        );

        if (activeChat) return (
            <Layout {...shellProps} activeKey="messages">
                <Chat 
                    token={token} 
                    otherUser={activeChat} 
                    onBack={() => { setActiveChat(null); setActiveSection(chatReturnRef.current || 'matches'); }} 
                />
            </Layout>
        );

        if (activeSection === 'matches') return (
            <Layout {...shellProps} activeKey="matches">
                <MyMatches 
                    matches={allMatches}
                    onBack={() => setActiveSection('home')}
                    onChat={(match) => { chatReturnRef.current = 'matches'; setActiveChat(match); }}
                />
            </Layout>
        );

        if (activeSection === 'messages') return (
            <Layout {...shellProps} activeKey="messages">
                <Messages 
                    matches={allMatches}
                    latest={unreadLatest}
                    unreadCount={unreadCount}
                    onChat={(match) => { chatReturnRef.current = 'messages'; setActiveChat(match); }}
                />
            </Layout>
        );

        if (selectedEventForReg) return (
            <Layout {...shellProps} activeKey="home">
                <EventRegistration 
                    event={selectedEventForReg} 
                    participant={participant}
                    onBack={() => {
                        setSelectedEventForReg(null);
                        setRegisterEventId(null);
                        setActiveSection('home');
                        // Clean eventId from URL when going back
                        const params = new URLSearchParams(window.location.search);
                        params.delete('eventId');
                        const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
                        window.history.replaceState({}, '', newUrl);
                    }}
                    onRegistered={(updatedP) => {
                        setParticipant(updatedP);
                        setSelectedEventForReg(null);
                        setRegisterEventId(null);
                        const params = new URLSearchParams(window.location.search);
                        params.delete('eventId');
                        const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
                        window.history.replaceState({}, '', newUrl);
                        setActiveSection('home');
                    }}
                />
            </Layout>
        );

        const isGameActive = participant.Event?.status >= 3 && participant.Event?.status < 5;

        // Merge all sources of participations to be sure we show everything
        const mergedParts = [
            ...(participant.Participations || []),
            ...(participant.participations || []),
            ...(allParticipations || [])
        ];
        // Unique by id
        const pastParticipations = Array.from(new Map(mergedParts.map(p => [p.id, p])).values());

        if (!participant.EventId || !isGameActive) {
            // Sezione “I Miei Eventi” (storico + match)
            if (activeSection === 'my-events') return (
                <Layout {...shellProps} activeKey="my-events">
                    <div className="mx-auto w-100 fade-in" style={{ maxWidth: '800px' }}>
                        <h2 className="app-section-title"><i className="fas fa-history"></i> I Tuoi Eventi</h2>
                        {pastParticipations.length > 0 ? (
                            <PastEvents participations={pastParticipations} onRevealMatches={(p) => setRevealingMatchesFor(p)} />
                        ) : (
                            <div className="card glass-premium border-0 p-5 text-center shadow-lg fade-in-up" style={{ borderRadius: '45px' }}>
                                <i className="fas fa-calendar-times fa-3x text-muted mb-3 opacity-20"></i>
                                <p className="text-muted fw-600 mb-0">Non hai ancora partecipato ad alcun evento. Il tuo storico apparirà qui dopo il primo evento!</p>
                            </div>
                        )}
                    </div>
                </Layout>
            );

            // Home: eventi disponibili
            return (
                <Layout {...shellProps} activeKey="home">
                    <FutureEvents 
                        participant={participant} 
                        futureEvents={futureEvents} 
                        handleLogout={handleLogout} 
                        onEditProfile={() => setIsEditingProfile(true)}
                        onSelectEvent={(ev) => setSelectedEventForReg(ev)}
                        onShowMatches={() => handleNavigate('matches')}
                        showMyEvents={pastParticipations.length > 0}
                        onShowMyEvents={() => setActiveSection('my-events')}
                    />
                </Layout>
            );
        }

        // Gioco attivo
        return (
            <Layout {...shellProps} menuItems={buildGameMenuItems()} activeKey="matches">
                <ActiveGame 
                    participant={participant}
                    others={others}
                    results={results}
                    loading={loading}
                    noteDrafts={noteDrafts}
                    handleNoteChange={handleNoteChange}
                    handleVote={handleVote}
                    handleVerify={handleVerify}
                    handleLogout={handleLogout}
                    onEditProfile={() => setIsEditingProfile(true)}
                    onShowMatches={() => handleNavigate('matches')}
                    shareLastName={shareLastName}
                    sharePhone={sharePhone}
                    privacyAccepted={privacyAccepted}
                    onSavePreferences={handleSavePreferences}
                    preferencesSaved={preferencesSaved}
                />
            </Layout>
        );
    }

    // 2. No participant - Auth Flow
    return (
        <Layout unreadCount={unreadCount} onBellClick={participant ? handleBellClick : null}>
            {needsRegistration ? (
                <Registration 
                    onComplete={handleCompleteRegistration} 
                    initialEmail={email} 
                    initialData={registrationData}
                    loading={loading}
                />
            ) : participations.length > 1 ? (
                <div className="card glass-premium border-0 p-5 text-center shadow-lg fade-in-up" style={{ borderRadius: '45px' }}>
                    <Branding />
                    <h2 className="fw-900 text-dark mt-4 mb-4">Scegli il tuo Evento</h2>
                    <div className="list-group list-group-flush gap-3">
                        {participations.map(p => (
                            <button key={p.id} className="list-group-item list-group-item-action border-0 rounded-4 p-4 shadow-sm text-start transition-all hover-scale" onClick={() => handleVerify(p.token)}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="fw-900 mb-1">{p.Event?.name}</h5>
                                        <small className="text-muted fw-600"><i className="fas fa-calendar-alt me-2"></i>{new Date(p.Event?.date).toLocaleDateString()}</small>
                                    </div>
                                    <i className="fas fa-chevron-right text-pink-600"></i>
                                </div>
                            </button>
                        ))}
                    </div>
                    <button className="btn btn-link mt-4 text-muted fw-700" onClick={handleLogout}>Torna al Login</button>
                </div>
            ) : (
                <div className="fade-in">
                    {loginMode === 'token' ? (
                        <LoginCode 
                            token={token} 
                            setToken={setToken} 
                            loading={loading} 
                            onVerify={() => handleVerify(token)} 
                            loginMode={loginMode} 
                            setLoginMode={setLoginMode} 
                            setOtpSent={setOtpSent} 
                            isForcedEmail={isForcedEmail}
                        />
                    ) : (
                        <LoginEmail 
                            email={email} 
                            setEmail={setEmail} 
                            otp={otp}
                            setOtp={setOtp}
                            loading={loading} 
                            otpSent={otpSent} 
                            setOtpSent={setOtpSent} 
                            handleRequestOTP={handleLoginEmail} 
                            handleVerifyOTP={handleVerifyOTP} 
                            loginMode={loginMode} 
                            setLoginMode={setLoginMode} 
                            isForcedEmail={isForcedEmail}
                        />
                    )}
                </div>
            )}
        </Layout>
    );
}
